export type SetupProofOwnerRoute = '/import' | '/exchange-sync';

export interface CsvImportSetupHandoff {
  kind: 'csv-import';
  occurredAt: string;
  sourceLabel: string;
  imported: number;
  skipped: number;
  errors: number;
  jobId?: string | null;
  ownerRoute?: '/import';
}

interface ExchangeSetupHandoffBase {
  occurredAt: string;
  exchangeId?: string | null;
  exchangeLabel: string;
  imported: number;
  skipped: number;
  errors: number;
  blocker?: 'AUTH_SESSION_BLOCKED' | 'SAFE_EXCHANGE_REPLAY_BLOCKED';
  blockerClassification?: 'safe_exchange_replay_blocked' | 'credential_action' | 'auth_action' | null;
  freshnessState?: 'healthy' | 'stale_verified' | 'never_synced' | null;
  freshnessStaleHours?: number | null;
  runnerWaitState?: 'runner_limited_wait' | null;
  blockerDetail?: string;
  ownerRoute?: SetupProofOwnerRoute;
}

export interface ApiSyncSetupHandoff extends ExchangeSetupHandoffBase {
  kind: 'api-sync';
}

export interface ApiDisconnectSetupHandoff extends ExchangeSetupHandoffBase {
  kind: 'api-disconnect';
}

export type ExchangeSetupHandoff = ApiSyncSetupHandoff | ApiDisconnectSetupHandoff;

export interface SetupMutationProofSnapshot {
  kind: 'csv-import' | 'api-sync' | 'api-disconnect';
  sourceLabel: string;
  exchangeId?: string | null;
  occurredAt: string;
  imported: number;
  skipped: number;
  errors: number;
  blocker?: 'AUTH_SESSION_BLOCKED' | 'SAFE_EXCHANGE_REPLAY_BLOCKED';
  blockerClassification?: 'safe_exchange_replay_blocked' | 'credential_action' | 'auth_action' | null;
  freshnessState?: 'healthy' | 'stale_verified' | 'never_synced' | null;
  freshnessStaleHours?: number | null;
  runnerWaitState?: 'runner_limited_wait' | null;
  blockerDetail?: string;
  ownerRoute?: SetupProofOwnerRoute;
  resultLabel?: 'Connection removed' | null;
  jobId?: string | null;
}

export type SetupHandoffContext =
  | CsvImportSetupHandoff
  | ExchangeSetupHandoff
  | {
      kind: 'goal-created' | 'goal-updated' | 'goal-activated';
      occurredAt: string;
      goalName: string;
      allocationTotal: number;
      goalCount: number;
      maxGoals: number;
      isActive: boolean;
      compatibilityMirrored: boolean;
    }
  | {
      kind: 'goal-deleted';
      occurredAt: string;
      goalName: string;
      goalCount: number;
      maxGoals: number;
      activeGoalName?: string | null;
      compatibilityMirrored: boolean;
    };

export const SETUP_HANDOFF_UPDATED_EVENT = 'pinecurl:setup-handoff-updated';

export function buildCsvImportSetupHandoff(input: {
  occurredAt: string;
  sourceLabel: string;
  imported: number;
  skipped: number;
  errors: number;
  jobId?: string | null;
  ownerRoute?: '/import';
}): CsvImportSetupHandoff {
  return {
    kind: 'csv-import',
    occurredAt: input.occurredAt,
    sourceLabel: input.sourceLabel,
    imported: input.imported,
    skipped: input.skipped,
    errors: input.errors,
    jobId: input.jobId ?? null,
    ownerRoute: input.ownerRoute ?? '/import',
  };
}

export function buildExchangeSetupHandoff(input: {
  kind?: 'api-sync' | 'api-disconnect';
  occurredAt: string;
  exchangeId?: string | null;
  exchangeLabel: string;
  imported: number;
  skipped: number;
  errors: number;
  blocker?: 'AUTH_SESSION_BLOCKED' | 'SAFE_EXCHANGE_REPLAY_BLOCKED';
  blockerClassification?: 'safe_exchange_replay_blocked' | 'credential_action' | 'auth_action' | null;
  freshnessState?: 'healthy' | 'stale_verified' | 'never_synced' | null;
  freshnessStaleHours?: number | null;
  runnerWaitState?: 'runner_limited_wait' | null;
  blockerDetail?: string;
  ownerRoute?: SetupProofOwnerRoute;
}): ExchangeSetupHandoff {
  const base = {
    occurredAt: input.occurredAt,
    exchangeId: input.exchangeId ?? null,
    exchangeLabel: input.exchangeLabel,
    imported: input.imported,
    skipped: input.skipped,
    errors: input.errors,
    blocker: input.blocker,
    blockerClassification: input.blockerClassification ?? null,
    freshnessState: input.freshnessState ?? null,
    freshnessStaleHours: input.freshnessStaleHours ?? null,
    runnerWaitState: input.runnerWaitState ?? null,
    blockerDetail: input.blockerDetail,
    ownerRoute: input.ownerRoute,
  } satisfies ExchangeSetupHandoffBase;

  if (input.kind === 'api-disconnect') {
    return {
      kind: 'api-disconnect',
      ...base,
    };
  }

  return {
    kind: 'api-sync',
    ...base,
  };
}

export function isSetupMutationHandoff(
  value: SetupHandoffContext | null | undefined,
): value is CsvImportSetupHandoff | ExchangeSetupHandoff {
  return Boolean(
    value
      && (
        value.kind === 'csv-import'
        || value.kind === 'api-sync'
        || value.kind === 'api-disconnect'
      ),
  );
}

export function getSetupMutationSourceLabel(handoff: CsvImportSetupHandoff | ExchangeSetupHandoff): string {
  return handoff.kind === 'csv-import' ? handoff.sourceLabel : handoff.exchangeLabel;
}

export function getSetupMutationExchangeId(
  handoff: ExchangeSetupHandoff | null | undefined,
): string | null {
  if (!handoff) {
    return null;
  }

  if (handoff.exchangeId && handoff.exchangeId.trim()) {
    return handoff.exchangeId;
  }

  const normalized = handoff.exchangeLabel.trim().toLowerCase().replace(/\s+exchange sync$/, '');
  return normalized || null;
}

export function buildSetupMutationProofSnapshot(
  handoff: SetupHandoffContext | null | undefined,
): SetupMutationProofSnapshot | null {
  if (!isSetupMutationHandoff(handoff)) {
    return null;
  }

  if (handoff.kind === 'csv-import') {
    return {
      kind: handoff.kind,
      sourceLabel: handoff.sourceLabel,
      occurredAt: handoff.occurredAt,
      imported: handoff.imported,
      skipped: handoff.skipped,
      errors: handoff.errors,
      ownerRoute: handoff.ownerRoute,
      jobId: handoff.jobId ?? null,
      resultLabel: null,
    };
  }

  return {
    kind: handoff.kind,
    sourceLabel: handoff.exchangeLabel,
    exchangeId: getSetupMutationExchangeId(handoff),
    occurredAt: handoff.occurredAt,
    imported: handoff.imported,
    skipped: handoff.skipped,
    errors: handoff.errors,
    blocker: handoff.blocker,
    blockerClassification: handoff.blockerClassification ?? null,
    freshnessState: handoff.freshnessState ?? null,
    freshnessStaleHours: handoff.freshnessStaleHours ?? null,
    runnerWaitState: handoff.runnerWaitState ?? null,
    blockerDetail: handoff.blockerDetail,
    ownerRoute: handoff.ownerRoute,
    resultLabel: handoff.kind === 'api-disconnect' ? 'Connection removed' : null,
  };
}

function getSetupHandoffStorageKey(userId: string) {
  return `setup-handoff:${userId}`;
}

function notifySetupHandoffUpdated(userId: string) {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new CustomEvent(SETUP_HANDOFF_UPDATED_EVENT, {
    detail: { userId },
  }));
}

export function readSetupHandoff(userId?: string | null): SetupHandoffContext | null {
  if (!userId || typeof window === 'undefined') return null;

  try {
    const raw = window.sessionStorage.getItem(getSetupHandoffStorageKey(userId));
    return raw ? (JSON.parse(raw) as SetupHandoffContext) : null;
  } catch {
    return null;
  }
}

export function writeSetupHandoff(userId: string | null | undefined, value: SetupHandoffContext | null) {
  if (!userId || typeof window === 'undefined') return;

  try {
    if (value == null) {
      window.sessionStorage.removeItem(getSetupHandoffStorageKey(userId));
      notifySetupHandoffUpdated(userId);
      return;
    }

    window.sessionStorage.setItem(getSetupHandoffStorageKey(userId), JSON.stringify(value));
    notifySetupHandoffUpdated(userId);
  } catch {
    // noop
  }
}

export function subscribeToSetupHandoff(
  userId: string | null | undefined,
  onChange: (value: SetupHandoffContext | null) => void,
) {
  if (!userId || typeof window === 'undefined') {
    return () => {};
  }

  const storageKey = getSetupHandoffStorageKey(userId);
  const handleChange = () => {
    onChange(readSetupHandoff(userId));
  };
  const handleStorage = (event: StorageEvent) => {
    if (event.storageArea !== window.sessionStorage || event.key !== storageKey) {
      return;
    }

    handleChange();
  };
  const handleUpdated = (event: Event) => {
    const detail = (event as CustomEvent<{ userId?: string }>).detail;
    if (detail?.userId !== userId) {
      return;
    }

    handleChange();
  };

  window.addEventListener('storage', handleStorage);
  window.addEventListener(SETUP_HANDOFF_UPDATED_EVENT, handleUpdated as EventListener);

  return () => {
    window.removeEventListener('storage', handleStorage);
    window.removeEventListener(SETUP_HANDOFF_UPDATED_EVENT, handleUpdated as EventListener);
  };
}

export function formatSetupHandoffTimestamp(value: string | null | undefined): string {
  if (!value) return '—';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';

  return parsed.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function buildSetupHandoffTransactionsHref(
  handoff: SetupHandoffContext | null | undefined,
): string | null {
  if (!isSetupMutationHandoff(handoff) || handoff.kind === 'api-disconnect') {
    return null;
  }

  const searchParams = new URLSearchParams();
  searchParams.set('reconciliation_only', '1');

  if (handoff.kind === 'csv-import') {
    searchParams.set('reconciliation_source', 'csv_import');

    if (handoff.jobId) {
      searchParams.set('reconciliation_job_id', handoff.jobId);
    }

    if (handoff.occurredAt) {
      searchParams.set('reconciliation_imported_after', handoff.occurredAt);
    }

    return `/transactions?${searchParams.toString()}`;
  }

  const exchangeId = getSetupMutationExchangeId(handoff);
  if (exchangeId) {
    searchParams.set('reconciliation_exchange', exchangeId);
  }

  if (handoff.occurredAt) {
    searchParams.set('reconciliation_imported_after', handoff.occurredAt);
  }

  searchParams.set('reconciliation_source', 'exchange_sync');

  return `/transactions?${searchParams.toString()}`;
}
